<template>
  <div fluid class="pa-0 mx-0">
    <div class="margin-custom">
      <v-row justify="center">
        <v-col cols="12" class="text-left">
          <div class="header-text">About</div>
          <div class="subheader-text">Who We Are</div>
          <div class="body-text">
            MITRE evaluates cybersecurity products using an open methodology based on the
            <a href="https://attack.mitre.org/" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">ATT&CK® knowledge base</a>.
            <br>
            Our mission is to improve organizations' performance against known adversary behaviors by:
          </div>
        </v-col>
      </v-row>
      <v-row class="image-row">
        <v-col :cols="12" :md="4" class="text-left">
          <img :src="require('../../assets/images/Empower.png')" alt="Empower" class="image" />
          <div class="mt-4">
            <div class="image-text-heading">Empowering End Users</div>
            <div class="image-text-body">Providing objective insights into how to use participating security products</div>
          </div>
        </v-col>
        <v-col :cols="12" :md="4" class="text-left">
          <img :src="require('../../assets/images/Transparency.png')" alt="Transparency" class="image" />
          <div class="mt-4">
            <div class="image-text-heading">Providing Transparency</div>
            <div class="image-text-body">Revealing the true capabilities of participating security products</div>
          </div>
        </v-col>
        <v-col :cols="12" :md="4" class="text-left">
          <img :src="require('../../assets/images/Enhance.png')" alt="Enhance" class="image" />
          <div class="mt-4">
            <div class="image-text-heading">Fostering Continual Improvement</div>
            <div class="image-text-body">Driving participants to enhance their capabilities</div>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <p class="evals-description">
            These evaluations are not a competitive analysis—there is no true "winner." ATT&amp;CK Evaluations show how each vendor participant approaches threat defense
            within the context of ATT&amp;CK because there is no singular way for analyzing, ranking, or rating the solutions.
            Our evaluation <a href="/methodology-overview" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">methodologies</a> are available, and the results are released. We are continuously evolving and expanding our
            methods and content to ensure a fair, transparent, and valuable evaluation process.
          </p>
        </v-col>
      </v-row>

      <v-row class="my-10">
        <v-col cols="12">
          <div class="header-text-medium">What You Should Know</div>
          <div class="text-left mb-6">
            <div class="header-text-small">ATT&amp;CK Evaluations are a starting point</div>
            <div class="body-text-small">We use an open-book and minimally sized environment to understand baseline capabilities of solutions.
            <br>
            Operationalization of these solutions is important to consider in the context of your organization, including false positive generation.</div>
          </div>
          <div class="text-left mb-6">
            <div class="header-text-small">ATT&amp;CK evaluations are objective</div>
            <div class="body-text-small">The evaluations represent the capabilities and limitations of each vendor participant.</div>
          </div>
          <div class="text-left mb-6">
            <div class="header-text-small">Context matters</div>
            <div class="body-text-small">A technique result for Credential Dumping may not have the same value as a technique result for Process Discovery, due to the severity of the action.
              <br>
              The result category provides a general assessment, but each technique and detection will have its own unique details.
            </div>
          </div>
          <div class="text-left mb-6">
            <div class="header-text-small">Not all procedures are created equal</div>
            <div class="body-text-small">
              For example,
              <a :href="getTechniqueUrl('Process Discovery')" target="_blank" rel="noopener" style="text-decoration: underline;">Process Discovery (T1057)</a>
              via
              <a :href="getTechniqueUrl('Command-Line Interface')" target="_blank" rel="noopener" style="text-decoration: underline;">Command-Line Interface (T1059)</a>
              can be detected with most process monitoring.
              <br>
              <a :href="getTechniqueUrl('Process Discovery')" target="_blank" rel="noopener" style="text-decoration: underline;">Process Discovery</a>
              via
              <a :href="getTechniqueUrl('API')" target="_blank" rel="noopener" style="text-decoration: underline;">API (T1106)</a>
              would need API Monitoring. A vendor could have a detection for one but not the other.
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="my-10">
        <v-col cols="12">
          <div class="header-text-medium">How It Works</div>
          <div class="body-text-small">
          Each participant has their own analytic approach of the results, and every user has their own criteria for success.
          <br>
          To accompany the raw results, ATT&amp;CK Evaluations has tools and resources that can help you use and understand the data.
          </div>
        </v-col>
      </v-row>
    </div>
    <div>
      <v-row class="navy-div">
        <div cols="12" class="text-center">
          <div class="our-evaluations">Featured</div>
        </div>
        <div class="image-div">
          <div class="d-flex">
            <div :cols="12" :md="4" :lg="2" class="text-center" v-for="(adversary, index) in mostRecentlyPublishedAdvs" :key="index">
              <div class="image-container">
                <img :src="$imageSrc + adversary.logo_image" :alt="'Evaluation - ' + adversary.name" class="evals-image" />
                <div class="links-section mt-4">
                  <router-link :to="`/${getURLFromDomain(adversary.domain).urlDomain}/${adversary.name}`" class="link">Overview</router-link> |
                  <router-link :to="`/results/${getURLFromDomain(adversary.domain).urlDomain}/?evaluation=${adversary.name}`" class="link">Results</router-link>
                  <span v-if="adversary.blog_url"> | </span>
                  <a target=_blank :href="adversary.blog_url" class="link" v-if="adversary.blog_url">Blog</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-row>

    </div>
    <Questions />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import store from '../store/index'
import { getURLFromDomain } from '../mixins'
import Questions from './home/Questions.vue'

const allAdversaries = computed (()=> store.getters.allAdversaries)
const mostRecentlyPublishedAdvs = computed(() => {
  if (allAdversaries.value.length) {
    const localAllAdversaries = allAdversaries.value;
    return localAllAdversaries
      .sort((a, b) => new Date(b.date_published) - new Date(a.date_published))
      .slice(0, 3);
  } else {
    return [];
  }
});

const techniqueMap = {
  'Process Discovery': 'T1057',
  'Command-Line Interface': 'T1059',
  'API': 'T1106',
};

const getTechniqueUrl = (techniqueName) => {
  const techniqueId = techniqueMap[techniqueName];
  return techniqueId ? `https://attack.mitre.org/techniques/${techniqueId}/` : '#';
};
</script>

<style scoped lang="scss">
  @import "../sass/pages/_about.scss";
</style>